import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { QueueListIcon, QrCodeIcon, ClockIcon, HomeIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import '../styles/comprehensive-responsive-system.css';

const CustomerBottomNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, currentThemeName } = useTheme();
  
  // Only show Home button when using modernDashboard theme
  const isModernTheme = currentThemeName === 'modernDashboard';

  // Check for latest pending or in-process order only - we only care about the most recent one
  useEffect(() => {
    // Don't make API calls if user is not loaded or is a vendor
    if (!user || user.roles?.includes('ROLE_VENDOR')) {
      return;
    }
    // Check for PENDING orders first (top priority)
    const statusParams = new URLSearchParams();
    statusParams.append('statuses', 'PENDING');

    // Fetch pending orders
    api.get(`/orders?${statusParams.toString()}`)
      .then(res => {
        if (res.data && res.data.length > 0) {
          // We found a pending order, no need to check for others
        } else {
          // No pending orders, check for in-process orders (ACCEPTED, PREPARING, READY)
          const inProcessParams = new URLSearchParams();
          ['ACCEPTED', 'PREPARING', 'READY'].forEach(status =>
            inProcessParams.append('statuses', status)
          );

          api.get(`/orders?${inProcessParams.toString()}`)
            .then(inProcessRes => {
              if (inProcessRes.data && inProcessRes.data.length > 0) {
                // Found in-process orders
              } else {
                // No pending or in-process orders
              }
            })
            .catch(err => {
              console.error("Failed to check for in-process orders:", err);
            });
        }
      })
      .catch(err => {
        console.error("Failed to check for pending orders:", err);
      });
  }, [location.pathname, user]); // Re-check when the path changes or user changes

  // Define paths for navigation
  const homePath = '/customer/dashboard';
  const scanPath = '/scan';
  const ordersPath = '/orders';
  const qrPath = '/qr'; // The QR code scanning page is the primary menu entry point

  // Determine which tab should be active
  const isHomeActive = location.pathname === homePath;
  const isOnMenuPage = location.pathname.startsWith('/menu');
  const isOnQrPage = location.pathname === qrPath;
  // The Menu tab should be active when user is on either the vendor menu or the QR page
  const isMenuActive = isOnMenuPage || isOnQrPage;
  const isScanActive = location.pathname === scanPath;
  const isOrdersActive = location.pathname.startsWith('/orders');

  // Adding a class to the body to add padding at the bottom
  useEffect(() => {
    document.body.classList.add('has-bottom-nav');
    return () => {
      document.body.classList.remove('has-bottom-nav');
    };
  }, []);

  const handleScanClick = () => {
    // Clear vendor context from localStorage before navigating
    localStorage.removeItem('vendorId');
    localStorage.removeItem('qrCode');
    navigate(scanPath);
  };

  return (
    <div
      className="fixed bottom-0 left-0 w-full z-[1002] flex justify-around items-center lg:hidden h-16 vendor-bottom-nav"
      style={{
        backgroundColor: theme.background,
        fontFamily: 'var(--font-family-base)'
      }}
    >
      {/* Home Tab - only visible when modernDashboard theme is active */}
      {isModernTheme && (
        <Link
          to={homePath}
          className="flex flex-col items-center justify-center transition-all duration-200 rounded-lg relative group py-responsive-sm px-responsive-sm touch-target"
          style={{
            fontSize: '0.75rem',
            color: isHomeActive ? theme.primary : theme.gray?.[600] || '#666666',
            fontWeight: isHomeActive ? '600' : '400',
            backgroundColor: isHomeActive ? theme.button.secondary.bg : 'transparent'
          }}
          onMouseEnter={(e) => {
            if (!isHomeActive) {
              e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f5f5f5';
            }
          }}
          onMouseLeave={(e) => {
            if (!isHomeActive) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <HomeIcon className={`w-6 h-6 mb-1 transition-transform duration-200 ${isHomeActive ? 'scale-110' : 'group-hover:scale-105'}`} />
          <span className="tracking-wide">Home</span>
          {isHomeActive && (
            <div
              className="absolute left-2 right-2 h-[2px] rounded-full"
              style={{ bottom: '-3px', backgroundColor: theme.primary }}
            />
          )}
        </Link>
      )}

      {/* Menu Tab */}
      <Link
        to={qrPath}
        className="flex flex-col items-center justify-center transition-all duration-200 rounded-lg relative group py-responsive-sm px-responsive-sm touch-target"
        style={{
          fontSize: '0.75rem',
          color: isMenuActive ? theme.primary : theme.gray?.[600] || '#666666',
          fontWeight: isMenuActive ? '600' : '400',
          backgroundColor: isMenuActive ? theme.button.secondary.bg : 'transparent'
        }}
        onClick={() => {
          // Clear vendor context from localStorage when clicking Menu tab
          localStorage.removeItem('vendorId');
          localStorage.removeItem('qrCode');
        }}
        onMouseEnter={(e) => {
          if (!isMenuActive) {
            e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMenuActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <QueueListIcon className={`w-6 h-6 mb-1 transition-transform duration-200 ${isMenuActive ? 'scale-110' : 'group-hover:scale-105'}`} />
        <span className="tracking-wide">Menu</span>
        {isMenuActive && (
          <div
            className="absolute left-2 right-2 h-[2px] rounded-full"
            style={{ bottom: '-3px', backgroundColor: theme.primary }}
          />
        )}
      </Link>

      <button
        onClick={handleScanClick}
        className="flex flex-col items-center justify-center transition-all duration-200 rounded-lg relative group py-responsive-sm px-responsive-sm touch-target"
        style={{
          fontSize: '0.75rem',
          color: isScanActive ? theme.primary : theme.gray?.[600] || '#666666',
          fontWeight: isScanActive ? '600' : '400',
          backgroundColor: isScanActive ? theme.button.secondary.bg : 'transparent',
          border: 'none',
          cursor: 'pointer'
        }}
        type="button"
        onMouseEnter={(e) => {
          if (!isScanActive) {
            e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!isScanActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <QrCodeIcon className={`w-6 h-6 mb-1 transition-transform duration-200 ${isScanActive ? 'scale-110' : 'group-hover:scale-105'}`} />
        <span className="tracking-wide">Scan</span>
        {isScanActive && (
          <div
            className="absolute left-2 right-2 h-[2px] rounded-full"
            style={{ bottom: '-3px', backgroundColor: theme.primary }}
          />
        )}
      </button>

      <Link
        to={ordersPath}
        className="flex flex-col items-center justify-center transition-all duration-200 rounded-lg relative group py-responsive-sm px-responsive-sm touch-target"
        style={{
          fontSize: '0.75rem',
          color: isOrdersActive ? theme.primary : theme.gray?.[600] || '#666666',
          fontWeight: isOrdersActive ? '600' : '400',
          backgroundColor: isOrdersActive ? theme.button.secondary.bg : 'transparent'
        }}
        onMouseEnter={(e) => {
          if (!isOrdersActive) {
            e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f5f5f5';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOrdersActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <ClockIcon className={`w-6 h-6 mb-1 transition-transform duration-200 ${isOrdersActive ? 'scale-110' : 'group-hover:scale-105'}`} />
        <span className="tracking-wide">My Orders</span>
        {isOrdersActive && (
          <div
            className="absolute left-2 right-2 h-[2px] rounded-full"
            style={{ bottom: '-3px', backgroundColor: theme.primary }}
          />
        )}
      </Link>
    </div>
  );
};

export default CustomerBottomNavigation;
